/**
 * SearchFilterToolbar - A reusable search and filter toolbar component
 *
 * Provides a compact horizontal toolbar with:
 * - Debounced search input with clear button
 * - Configurable toggle buttons (e.g., Show Filters, Show Metrics)
 * - Active filter indicator with clear action
 * - Fully customizable via props and render props
 *
 * @example
 * ```tsx
 * <SearchFilterToolbar
 *   searchValue={searchQuery}
 *   onSearchChange={setSearchQuery}
 *   searchPlaceholder="Search by name, symbol, or ID..."
 *   toggles={[
 *     { id: 'filters', label: 'Filters', icon: SlidersHorizontal, isActive: showFilters, onToggle: () => setShowFilters(!showFilters) },
 *     { id: 'metrics', label: 'Metrics', icon: Eye, isActive: showMetrics, onToggle: () => setShowMetrics(!showMetrics) },
 *   ]}
 *   hasActiveFilters={hasFilters}
 *   onClearFilters={handleClearFilters}
 * />
 * ```
 */

import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { FilterX, Search, X, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";

// =============================================================================
// Types
// =============================================================================

export interface ToggleButtonConfig {
  /** Unique identifier for the toggle */
  id: string;
  /** Display label for the button */
  label: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Whether the toggle is currently active */
  isActive: boolean;
  /** Callback when toggle is clicked */
  onToggle: () => void;
  /** Optional badge count to show on the button */
  badgeCount?: number;
  /** Optional aria-label for accessibility */
  ariaLabel?: string;
}

export interface SearchFilterToolbarProps {
  /** Current search input value */
  searchValue: string;
  /** Callback when search value changes */
  onSearchChange: (value: string) => void;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Array of toggle button configurations */
  toggles?: ToggleButtonConfig[];
  /** Whether there are active filters (shows clear button) */
  hasActiveFilters?: boolean;
  /** Total count of active filters to display */
  activeFilterCount?: number;
  /** Callback to clear all filters */
  onClearFilters?: () => void;
  /** Custom className for the container */
  className?: string;
  /** Additional actions to render on the right side */
  renderActions?: () => ReactNode;
  /** Input ID for accessibility (default: auto-generated) */
  inputId?: string;
  /** Whether search is disabled */
  disabled?: boolean;
  /** Size variant for the toolbar */
  size?: "sm" | "md" | "lg";
}

// =============================================================================
// Hook: useDebounce
// =============================================================================

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// =============================================================================
// Sub-components
// =============================================================================

interface ToggleButtonProps {
  config: ToggleButtonConfig;
  size: "sm" | "md" | "lg";
}

const ToggleButton = memo(function ToggleButton({
  config,
  size,
}: ToggleButtonProps) {
  const Icon = config.icon;

  const sizeClasses = {
    sm: "h-8 text-xs gap-1.5 px-2.5",
    md: "h-10 text-sm gap-2 px-3",
    lg: "h-11 text-sm gap-2 px-4",
  };

  const iconSizes = {
    sm: "h-3.5 w-3.5",
    md: "h-4 w-4",
    lg: "h-4 w-4",
  };

  return (
    <Button
      variant={config.isActive ? "default" : "outline"}
      onClick={config.onToggle}
      aria-label={config.ariaLabel || `Toggle ${config.label}`}
      aria-pressed={config.isActive}
      className={cn(
        "relative flex items-center transition-all",
        sizeClasses[size],
        config.isActive
          ? "bg-gradient-to-r from-brand-blue to-brand-blue-dark border-brand-blue"
          : "border-white/10 hover:border-white/20 bg-dark-900/50 hover:bg-dark-800/50"
      )}
    >
      <Icon className={iconSizes[size]} />
      <span>{config.isActive ? "Hide" : "Show"} {config.label}</span>
      {config.badgeCount !== undefined && config.badgeCount > 0 && (
        <Badge
          variant="secondary"
          className="ml-1 h-5 min-w-5 px-1.5 text-xs bg-white/10"
        >
          {config.badgeCount}
        </Badge>
      )}
    </Button>
  );
});

// =============================================================================
// Main Component
// =============================================================================

export const SearchFilterToolbar = memo(function SearchFilterToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  debounceMs = 300,
  toggles = [],
  hasActiveFilters = false,
  activeFilterCount,
  onClearFilters,
  className,
  renderActions,
  inputId,
  disabled = false,
  size = "md",
}: SearchFilterToolbarProps) {
  // Local state for immediate input feedback
  const [localValue, setLocalValue] = useState(searchValue);

  // Track whether the current value originated from user input (vs external prop change)
  const isUserInputRef = useRef(false);

  // When searchValue changes externally, this effect marks the change as external and updates localValue.
  // The ref is set synchronously before the debounced value propagates and triggers the sync-to-parent effect.
  useEffect(() => {
    isUserInputRef.current = false; // Mark as external change
    setLocalValue(searchValue);
  }, [searchValue]);

  // Debounce the local value and call parent callback
  const debouncedValue = useDebounce(localValue, debounceMs);

  useEffect(() => {
    // Only sync debounced value to parent if:
    // 1. The debounced value differs from parent's current value
    // 2. AND the change originated from user input (not an external prop change)
    if (debouncedValue !== searchValue && isUserInputRef.current) {
      onSearchChange(debouncedValue);
    }
  }, [debouncedValue, onSearchChange, searchValue]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      isUserInputRef.current = true; // Mark as user input
      setLocalValue(e.target.value);
    },
    []
  );

  const handleClearSearch = useCallback(() => {
    isUserInputRef.current = true; // Mark as user input (the X button is a user action)
    setLocalValue("");
    onSearchChange("");
  }, [onSearchChange]);

  // Size-based styles
  const inputSizes = {
    sm: "h-8 text-sm",
    md: "h-10",
    lg: "h-11 text-base",
  };

  const iconSizes = {
    sm: "h-3.5 w-3.5",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  const containerPadding = {
    sm: "p-3",
    md: "p-4",
    lg: "p-5",
  };

  const generatedInputId = inputId || `search-filter-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row gap-3",
        containerPadding[size],
        className
      )}
      role="search"
      aria-label="Search and filter toolbar"
    >
      {/* Search Input */}
      <div className="flex-1 min-w-0">
        <div className="relative">
          <Search
            className={cn(
              "absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground",
              iconSizes[size]
            )}
            aria-hidden="true"
          />
          <Input
            id={generatedInputId}
            type="text"
            placeholder={searchPlaceholder}
            value={localValue}
            onChange={handleInputChange}
            disabled={disabled}
            aria-label="Search"
            className={cn(
              "pl-10 pr-10 bg-dark-900/50 border-white/10 focus:border-brand-blue transition-colors",
              inputSizes[size]
            )}
          />
          {localValue && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-white/10"
              onClick={handleClearSearch}
              aria-label="Clear search"
              disabled={disabled}
            >
              <X className={iconSizes[size]} />
            </Button>
          )}
        </div>
      </div>

      {/* Toggle Buttons & Actions */}
      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        {/* Clear Filters Button - Only show when filters are active */}
        {hasActiveFilters && onClearFilters && (
          <Button
            variant="outline"
            onClick={onClearFilters}
            className={cn(
              "border-white/10 hover:border-white/20 bg-dark-900/50 hover:bg-dark-800/50",
              size === "sm" ? "h-8 text-xs px-2.5" : size === "lg" ? "h-11 px-4" : "h-10 px-3"
            )}
            aria-label={`Clear ${activeFilterCount || ""} active filters`}
          >
            <FilterX className={cn("mr-2", iconSizes[size])} />
            Clear{activeFilterCount ? ` (${activeFilterCount})` : ""}
          </Button>
        )}

        {/* Toggle Buttons */}
        {toggles.map((toggle) => (
          <ToggleButton key={toggle.id} config={toggle} size={size} />
        ))}

        {/* Custom Actions */}
        {renderActions?.()}
      </div>
    </div>
  );
});

// =============================================================================
// Compound Components for Advanced Usage
// =============================================================================

export interface SearchFilterToolbarContextValue {
  searchValue: string;
  onSearchChange: (value: string) => void;
  hasActiveFilters: boolean;
  onClearFilters?: () => void;
}

// Export types for external use
export type { LucideIcon };
